import { Clock, CalendarClock, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AvailabilityBannerType } from '@/hooks/useOrderAvailability';

interface OrderAvailabilityBannerProps {
  bannerType: AvailabilityBannerType;
  bannerMessage: string;
  onScheduleOrder: () => void;
  className?: string;
}

export function OrderAvailabilityBanner({ 
  bannerType,
  bannerMessage,
  onScheduleOrder,
  className
}: OrderAvailabilityBannerProps) {
  if (bannerType === 'none') {
    return null;
  }

  const config = getBannerConfig(bannerType);

  return (
    <div className={cn(
      "rounded-lg p-4 mb-4 border bg-muted/50",
      className
    )}>
      <div className="flex items-start gap-3">
        <div className={cn("p-2 rounded-full bg-muted")}>
          <config.Icon className={cn("w-5 h-5", config.iconClass)} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground">
            {config.title}
          </h3>
          <p className="text-sm mt-0.5 text-muted-foreground">
            {bannerMessage}
          </p>
        </div>
        {config.showScheduleButton && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={onScheduleOrder}
            className="shrink-0"
          >
            <CalendarClock className="w-4 h-4 mr-1.5" />
            {config.scheduleButtonLabel || 'Schedule Order'}
          </Button>
        )}
      </div>
    </div>
  );
}

interface BannerConfig {
  Icon: typeof Clock;
  title: string;
  iconClass: string;
  showScheduleButton: boolean;
  scheduleButtonLabel?: string;
}

function getBannerConfig(bannerType: AvailabilityBannerType): BannerConfig {
  switch (bannerType) {
    case 'opens-soon':
      return {
        Icon: CheckCircle,
        title: 'Opens Soon',
        iconClass: 'text-primary',
        showScheduleButton: false,
      };

    case 'closes-soon':
      return {
        Icon: AlertTriangle,
        title: 'Closes Soon',
        iconClass: 'text-primary',
        showScheduleButton: false,
      };

    case 'closed':
      return {
        Icon: Clock,
        title: 'Currently Closed',
        iconClass: 'text-muted-foreground',
        showScheduleButton: true,
      };

    case 'order-after-close':
      return {
        Icon: AlertTriangle,
        title: 'Order Won\'t Be Ready',
        iconClass: 'text-destructive',
        showScheduleButton: true,
      };

    case 'scheduled-unavailable':
      return {
        Icon: AlertTriangle,
        title: 'Time Selection Required',
        iconClass: 'text-muted-foreground',
        showScheduleButton: true,
      };

    case 'scheduled':
      return {
        Icon: CalendarClock,
        title: 'Scheduled Order',
        iconClass: 'text-primary',
        showScheduleButton: true,
        scheduleButtonLabel: 'Change Time',
      };

    default:
      return {
        Icon: Clock,
        title: 'Status Unknown',
        iconClass: 'text-muted-foreground',
        showScheduleButton: false,
      };
  }
}
